/**
 * Brand Kit endpoint helpers — the single generation-honored brand surface.
 *
 * BE source of truth:
 *   ``ando-agent/src/api/v1/endpoints/brand_kits.py`` (prefix ``/brand-kits``).
 *   A kit is per-org (or per-user) and carries everything that brands a
 *   generated doc/deck/sheet:
 *     - ``style``      — palette + fonts + technique toggles (StyleSpec)
 *     - ``doc_chrome`` — logo / header / footer / cover / page geometry
 *     - ``writing_style_name`` — the tone/voice (wired into the agent prompt)
 *     - ``ooxml_template_key`` — uploaded .docx/.pptx/.xlsx → merge-on-top
 *
 *   Resolution at generation time: user default → org default → builtin Ando.
 *   The builtin default is returned by ``GET /brand-kits/_effective`` with
 *   ``id: null`` (not persisted).
 */

import { apiClient } from "../ApiClient.js";

// ─── Spec shapes (mirror the BE Pydantic models) ──────────────────────────

export interface BrandKitStyle {
  accent: string;
  accent_deep: string;
  accent_muted: string;
  ink: string;
  ink_deep: string;
  ink_soft: string;
  surface: string;
  on_dark: string;
  heading_font: string;
  body_font: string;
  category: "warm" | "cool" | "neutral" | "dark";
  cards: boolean;
  morph: boolean;
  accent_motif: boolean;
  picture_shadow: boolean;
}

export interface BrandKitMargins {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface BrandKitDocChrome {
  logo_key: string | null;
  header_text: string | null;
  footer_text: string | null;
  show_page_number: boolean;
  cover_enabled: boolean;
  page_size: "A4" | "Letter";
  /** ``null`` = use the generator's floor margins (2.5cm). */
  margins: BrandKitMargins | null;
}

/** OOXML template formats a Brand Kit can carry — one slot each. */
export type BrandKitTemplateFormat = "docx" | "pptx" | "xlsx";

export interface BrandKitSpec {
  style: BrandKitStyle;
  doc_chrome: BrandKitDocChrome;
  writing_style_name: string | null;
  /**
   * LEGACY single-template slot (kept in sync by the BE on upload).
   * Superseded by ``ooxml_template_keys`` — read via a per-format lookup
   * with this as extension-matching fallback.
   */
  ooxml_template_key: string | null;
  /**
   * Jul 5, 2026 — ONE template slot PER FORMAT: a kit carries a Word
   * letterhead AND a PowerPoint deck AND an Excel workbook template at the
   * same time. Upload auto-slots by extension (BE); generation starts from
   * the slot matching the output format. Optional for old stored specs.
   */
  ooxml_template_keys?: Partial<Record<BrandKitTemplateFormat, string>>;
  /**
   * Custom uploaded font files (.ttf/.otf), one slot per role — MinIO keys.
   * Uploading also flips ``style.heading_font``/``style.body_font`` to the
   * file's real family name; at generation the files are installed in the
   * sandbox before the measure/render pass.
   */
  font_keys?: Partial<Record<"heading" | "body", string>>;
}

/**
 * The template key for ``fmt``: per-format slot first, else the legacy
 * single key when its extension matches. Mirrors the BE
 * ``BrandKitSpec.template_key_for`` (single source of slot semantics).
 */
export function brandKitTemplateKeyFor(
  spec: BrandKitSpec,
  fmt: BrandKitTemplateFormat,
): string | null {
  const slot = spec.ooxml_template_keys?.[fmt];
  if (slot) return slot;
  const legacy = spec.ooxml_template_key;
  return legacy?.toLowerCase().endsWith(`.${fmt}`) ? legacy : null;
}

export interface BrandKit {
  /** ``null`` for the builtin Ando default (not persisted). */
  id: string | null;
  owner_type: "user" | "org";
  slug: string;
  name: string;
  is_default: boolean;
  spec: BrandKitSpec;
}

export interface CreateBrandKitBody {
  name: string;
  spec?: BrandKitSpec;
  make_default?: boolean;
}

export interface UpdateBrandKitBody {
  name?: string;
  spec?: BrandKitSpec;
}

// ─── Fetchers ──────────────────────────────────────────────────────────────

/** All kits visible to the caller (org kits + personal kits), default first. */
export async function listBrandKits(): Promise<BrandKit[]> {
  return apiClient.get<BrandKit[]>("/brand-kits");
}

/** The resolved effective kit (user → org → builtin Ando). Always returns one. */
export async function getEffectiveBrandKit(): Promise<BrandKit> {
  return apiClient.get<BrandKit>("/brand-kits/_effective");
}

/** Create a kit (owner = org when the caller is in one, else user). */
export async function createBrandKit(body: CreateBrandKitBody): Promise<BrandKit> {
  return apiClient.post<BrandKit>("/brand-kits", body);
}

/** Partial update of name / spec. */
export async function updateBrandKit(
  slug: string,
  body: UpdateBrandKitBody,
): Promise<BrandKit> {
  return apiClient.patch<BrandKit>(`/brand-kits/${encodeURIComponent(slug)}`, body);
}

/** Make ``slug`` the owner's default kit. */
export async function setDefaultBrandKit(slug: string): Promise<BrandKit> {
  return apiClient.post<BrandKit>(
    `/brand-kits/${encodeURIComponent(slug)}/default`,
  );
}

/** Delete a kit — 204 on success. */
export async function deleteBrandKit(slug: string): Promise<void> {
  await apiClient.delete<void>(`/brand-kits/${encodeURIComponent(slug)}`);
}

/**
 * Upload the brand logo (PNG/JPEG/WebP/SVG). Multipart via ``request`` so the
 * browser sets the boundary (the JSON ``post`` helper would force the wrong
 * Content-Type). Returns the updated kit.
 */
export async function uploadBrandKitLogo(slug: string, file: File): Promise<BrandKit> {
  const form = new FormData();
  form.append("file", file, file.name);
  return apiClient.request<BrandKit>(
    `/brand-kits/${encodeURIComponent(slug)}/logo`,
    { method: "POST", body: form },
  );
}

/**
 * Upload a branded OOXML template (.docx/.pptx/.xlsx). The system edits ON TOP
 * of it at generation (``OfficeCliService.merge``), preserving its real design.
 */
/**
 * Upload a custom brand font (.ttf/.otf) for a role ("heading"/"body"). The BE
 * parses the file's REAL family name, sets it on the style tokens and mounts
 * the file at generation so decks render with the actual typography.
 */
/**
 * Download an OFL font from Google Fonts by family name (e.g. "Inter") and
 * slot it on the kit — same pipeline as a proprietary upload, the BE fetches
 * the .ttf (the render sandbox has no network).
 */
export async function downloadBrandKitGoogleFont(
  slug: string,
  family: string,
  role: "heading" | "body",
): Promise<BrandKit> {
  return apiClient.post<BrandKit>(
    `/brand-kits/${encodeURIComponent(slug)}/font/google`,
    { family, role },
  );
}

export async function uploadBrandKitFont(
  slug: string,
  file: File,
  role: "heading" | "body",
): Promise<BrandKit> {
  const form = new FormData();
  form.append("file", file, file.name);
  form.append("role", role);
  return apiClient.request<BrandKit>(
    `/brand-kits/${encodeURIComponent(slug)}/font`,
    { method: "POST", body: form },
  );
}

export async function uploadBrandKitTemplate(slug: string, file: File): Promise<BrandKit> {
  const form = new FormData();
  form.append("file", file, file.name);
  return apiClient.request<BrandKit>(
    `/brand-kits/${encodeURIComponent(slug)}/template`,
    { method: "POST", body: form },
  );
}
